import React, { createContext, useContext, useEffect, useState } from "react";

const CartContext = createContext({ items: [], add: () => {}, remove: () => {}, update: () => {}, clear: () => {}, count: 0 });

const STORAGE_KEY = "amazypet_cart";

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  const add = (product, qty = 1, variantId = null) => {
    setItems((prev) => {
      const matchKey = (i) =>
        i.product_id === product.id && (i.variant_id || null) === (variantId || null);
      const existing = prev.find(matchKey);
      if (existing) {
        return prev.map((i) => (matchKey(i) ? { ...i, quantity: i.quantity + qty } : i));
      }
      const variant = (product.variants || []).find((v) => v.id === variantId);
      const basePrice = product.sale_price || product.price;
      const itemPrice = basePrice + (variant?.price_diff || 0);
      const variantLabel = variant
        ? Object.values(variant.attributes || {}).join(" / ")
        : null;
      return [
        ...prev,
        {
          product_id: product.id,
          variant_id: variantId,
          variant_label: variantLabel,
          name_el: product.name_el,
          name_en: product.name_en,
          price: itemPrice,
          image: variant?.image || (product.images || [])[0],
          quantity: qty,
        },
      ];
    });
  };

  const remove = (product_id, variant_id = null) => {
    setItems((prev) =>
      prev.filter(
        (i) => !(i.product_id === product_id && (i.variant_id || null) === (variant_id || null))
      )
    );
  };

  const update = (product_id, quantity, variant_id = null) => {
    if (quantity <= 0) return remove(product_id, variant_id);
    setItems((prev) =>
      prev.map((i) =>
        i.product_id === product_id && (i.variant_id || null) === (variant_id || null)
          ? { ...i, quantity }
          : i
      )
    );
  };

  const clear = () => setItems([]);

  const count = items.reduce((s, i) => s + i.quantity, 0);
  const subtotal = items.reduce((s, i) => s + i.price * i.quantity, 0);

  return (
    <CartContext.Provider value={{ items, add, remove, update, clear, count, subtotal }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);
